import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSocIdentities, type SocIdentity } from '@/api/soc';
import { Chip, CompanyCell, Empty, ListPage, PageLoading, Th, tableWrap, theadCls, useListState, type FilterDef } from '@/components/network/NetworkKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { insightsPersonPath } from '@/lib/routes';

/**
 * Huntress Managed ITDR identities (Microsoft 365 accounts Huntress watches). MFA as Huntress sees it:
 * unknown is NOT "off" (Huntress leaves it blank for half the accounts). Names link to Person Orbit,
 * where the CIPP view of the same account sits beside it.
 */

const FILTERS: FilterDef<SocIdentity>[] = [
  { id: 'billable', label: 'Billable',          test: (i) => !!i.billable },
  { id: 'no-mfa',   label: 'No MFA (billable)', test: (i) => !!i.enabled && !!i.billable && i.mfa === false },
  { id: 'risk',     label: 'At risk',           test: (i) => !!i.riskLevel && i.riskLevel !== 'none' },
  { id: 'enabled',  label: 'Enabled',           test: (i) => !!i.enabled },
  { id: 'all',      label: 'All',               test: () => true },
];

const mfaChip = (m: boolean | null) => (m == null ? <Chip tone="muted" title="Huntress doesn't report MFA for this account">unknown</Chip> : m ? <Chip tone="good">on</Chip> : <Chip tone="bad">off</Chip>);

export default function SocIdentitiesPage() {
  const { data, isLoading, error, isFetching } = useSocIdentities({ scoped: true });
  const items = data?.items ?? [];
  const l = useListState('cosmos:soc:identities:show', FILTERS, items, (i) => `${i.upn ?? ''} ${i.orgName ?? ''} ${i.companyName ?? ''}`);
  const rows = l.rows.slice(0, 1000);
  const loadingCache = data?.configured && data.items == null;

  return (
    <ListPage area="SOC" title="Identities (ITDR)" count={data?.items ? l.rows.length : null} isFetching={isFetching}
      subtitle="Microsoft 365 accounts Huntress Managed ITDR watches"
      search={{ q: l.q, setQ: l.setQ, placeholder: 'UPN, client' }}
      filters={{ defs: FILTERS, show: l.show, pick: l.pick, counts: l.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={data?.configured} vendor="Huntress" />
      {loadingCache && <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading identities from Huntress (first load after a restart takes about a minute)…</div>}
      {data?.items && (
        <>
          <div className={tableWrap}>
            <table className="w-full text-sm" style={{ minWidth: 1000 }}>
              <thead className={theadCls}>
                <tr><Th>Account</Th><Th>Client</Th><Th>Status</Th><Th>MFA</Th><Th>Risk</Th><Th>Password changed</Th></tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {rows.length === 0 && <Empty cols={6} />}
                {rows.map((i) => (
                  <tr key={i.id} className="hover:bg-muted/30">
                    <td className="max-w-[360px] px-3 py-2">
                      {i.upn ? <Link to={insightsPersonPath(i.upn)} className="block truncate text-foreground hover:underline" title="Person Orbit">{i.upn}</Link> : '—'}
                      <div className="text-[11px] text-muted-foreground">{[i.external && 'external', i.synced && 'synced from AD'].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className="max-w-[220px] px-3 py-2 text-xs"><CompanyCell companyId={i.companyId} companyName={i.companyName} fallback={i.orgName} /></td>
                    <td className="px-3 py-2 text-xs">{i.enabled ? 'Enabled' : <span className="text-muted-foreground">Disabled</span>}{i.billable && <span className="text-muted-foreground"> · billable</span>}</td>
                    <td className="px-3 py-2">{mfaChip(i.mfa)}</td>
                    <td className="px-3 py-2 text-xs">{i.riskLevel && i.riskLevel !== 'none' ? <Chip tone={i.riskLevel === 'high' || i.riskLevel === 'medium' ? 'bad' : 'warn'}>{i.riskLevel} · {i.riskState}</Chip> : <span className="text-muted-foreground">{i.riskState ?? '—'}</span>}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{fmtRelative(i.passwordChanged)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {l.rows.length > rows.length && <p className="mt-2 text-xs text-muted-foreground">Showing the first {rows.length} of {l.rows.length}: search or filter to narrow.</p>}
        </>
      )}
    </ListPage>
  );
}
